/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { useCallback } from 'preact/hooks';
import { SelectionDetail } from '../UNSAFE_Collection';
import { SelectorAll } from '../UNSAFE_SelectorAll';
import { Text } from '../UNSAFE_Text';
import {
  TableHeaderRendererContext,
  TableSelectionDetail,
  SortCriterionDetail
} from '../UNSAFE_TableView';
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, isSameKey } from '../utils/UNSAFE_keys';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { useId } from '../hooks/UNSAFE_useId';
import { tableHeaderSortContainerStyles } from './themes/Table.css';
import { TableHeaderCellVariantOptions } from './themes/TableHeaderCellStyles.css';
import { TableHeaderCellRedwoodTheme } from './themes/redwood/TableHeaderCellTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { BundleType } from '../resources/nls/bundle';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { EmbeddedIconButton } from '../PRIVATE_EmbeddedIconButton';
import { SortAscendingIcon } from '../PRIVATE_ThemedIcons/SortAscendingIcon';
import { SortDefaultIcon } from '../PRIVATE_ThemedIcons/SortDefaultIcon';
import { SortDescendingIcon } from '../PRIVATE_ThemedIcons/SortDescendingIcon';

/**
 * Props for the TableHeaderCell Component
 */
export type TableHeaderCellProps<K, C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSelected: boolean;
  isPreviousSelected: boolean;
  isColumnSelectable: boolean;
  isRowSelectionEnabled: boolean;
  isFocusRingVisible: boolean;
  isTabbable: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  isRtl: boolean;
  headerText?: string;
  selectedColumnKeys?: Keys<C>;
  selectedRowKeys?: Keys<K>;
  stickyColumn?: 'start' | 'end' | 'none';
  renderer?: (context: TableHeaderRendererContext<C>) => ComponentChildren;
  onSelectionChange?: (detail: TableSelectionDetail<K, C>, isExtendableSelection: boolean) => void;
  sortable?: 'enabled' | 'disabled';
  sortDirection?: 'ascending' | 'descending';
  onSortCriterionChange?: (detail: SortCriterionDetail<C>) => void;
};

/**
 * The internal component used to render a single cell in a TableHeaderRow.
 */
export function TableHeaderCell<K, C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  hasVerticalGridlines,
  isSelected,
  isPreviousSelected,
  isColumnSelectable,
  isRowSelectionEnabled,
  selectedColumnKeys = { all: false, keys: new Set<C>() },
  selectedRowKeys = { all: false, keys: new Set<K>() },
  isFocusRingVisible,
  isTabbable,
  headerText,
  stickyColumn = 'none',
  renderer,
  onSelectionChange,
  sortable = 'disabled',
  sortDirection,
  onSortCriterionChange,
  isStickyStartEdge,
  isStickyEndEdge,
  isRtl
}: TableHeaderCellProps<K, C>) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const { interactionProps, applyActiveStyle, applyHoverStyle, applyPseudoHoverStyle } =
    useInteractionStyle();

  const hasRenderer = renderer != null;
  const { classes: themeClasses } = useComponentTheme<TableHeaderCellVariantOptions>(
    TableHeaderCellRedwoodTheme,
    {
      defaultRenderer: !hasRenderer ? 'isDefaultRenderer' : 'notDefaultRenderer',
      firstColumn: columnIndex === 0 ? 'isFirstColumn' : 'notFirstColumn',
      lastColumn: isFinalColumnIndex ? 'isLastColumn' : 'notLastColumn',
      borderStartSpacer:
        !(isPreviousSelected || isSelected) && (!hasVerticalGridlines || columnIndex === 0)
          ? 'isBorderStartSpacer'
          : 'notBorderStartSpacer',
      borderEndSpacer:
        isFinalColumnIndex && !isSelected ? 'isBorderEndSpacer' : 'notBorderEndSpacer',
      verticalGrid: hasVerticalGridlines ? 'isVerticalGrid' : 'notVerticalGrid',
      columnSelectable: isColumnSelectable ? 'isColumnSelectable' : 'notColumnSelectable',
      columnSelected: isSelected ? 'isColumnSelected' : 'notColumnSelected',
      previousColumnSelected: isPreviousSelected
        ? 'isPreviousColumnSelected'
        : 'notPreviousColumnSelected',
      stickyStartColumn: stickyColumn === 'start' ? 'isStickyStartColumn' : 'notStickyStartColumn',
      stickyEndColumn: stickyColumn === 'end' ? 'isStickyEndColumn' : 'notStickyEndColumn',
      stickyStartEdge: isStickyStartEdge ? 'isStickyStartEdge' : 'notStickyStartEdge',
      stickyEndEdge: isStickyEndEdge ? 'isStickyEndEdge' : 'notStickyEndEdge',
      focusRingVisible: isFocusRingVisible ? 'isFocusRingVisible' : 'notFocusRingVisible',
      active: applyActiveStyle ? 'isActive' : 'notActive',
      hover: applyHoverStyle ? 'isHover' : 'notHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      rtl: isRtl ? 'isRtl' : 'notRtl'
    }
  );

  const cellClasses = classNames([themeClasses]);

  let selectorAllState: 'all' | 'none' | 'partial' | 'partial-all';
  if (isSameKey(selectedRowKeys, { all: true, deletedKeys: new Set<K>() })) {
    selectorAllState = 'all';
  } else if (isSameKey(selectedRowKeys, { all: false, keys: new Set<K>() })) {
    selectorAllState = 'none';
  } else if (selectedRowKeys.all) {
    selectorAllState = 'partial-all';
  } else {
    selectorAllState = 'partial';
  }

  const handleSelectorChange = (detail: SelectionDetail<K>) => {
    if (onSelectionChange != null) {
      onSelectionChange(
        { value: { row: detail.value, column: selectedColumnKeys }, target: detail.target },
        false
      );
    }
  };

  const selectorRenderer = isRowSelectionEnabled
    ? () => (
        <SelectorAll
          aria-label={translations.collection_selectAllRows()}
          onChange={handleSelectorChange}
          selected={selectorAllState}
        />
      )
    : undefined;

  const sortIconRenderer =
    sortable === 'enabled' || sortDirection != null
      ? () => (
          <EmbeddedIconButton
            aria-label={
              sortable === 'enabled'
                ? translations.collection_sortEnabled()
                : translations.collection_sortDisabled()
            }
            isDisabled={sortable !== 'enabled'}
            onAction={handleSortGesture}>
            {sortDirection === 'ascending' ? (
              <SortAscendingIcon />
            ) : sortDirection === 'descending' ? (
              <SortDescendingIcon />
            ) : (
              <SortDefaultIcon />
            )}
          </EmbeddedIconButton>
        )
      : undefined;

  const headerCellRendererProps = {
    key: columnKey,
    headerText: headerText,
    sortable: sortable,
    sortDirection: sortDirection,
    selector: selectorRenderer,
    sortControl: sortIconRenderer
  };

  const handleSortGesture = useCallback(() => {
    if (onSortCriterionChange != null) {
      const newDirection = sortDirection === 'ascending' ? 'descending' : 'ascending';
      onSortCriterionChange([{ key: columnKey, sortDirection: newDirection }]);
    }
  }, [columnKey, sortDirection, onSortCriterionChange]);

  const getHeaderCellRenderer = useCallback(() => {
    if (renderer != null) {
      return renderer;
    }
    return ({ headerText, sortControl }: TableHeaderRendererContext<C>) => {
      const text =
        headerText != null ? (
          <Text overflowWrap="breakWord" lineClamp={3} variant="primary" size="md">
            {headerText}
          </Text>
        ) : null;
      if (sortControl != null) {
        return (
          <div class={tableHeaderSortContainerStyles.base}>
            {text}
            {sortControl()}
          </div>
        );
      }
      return text;
    };
  }, [renderer]);

  const getAriaSelected = () => {
    if (isSelected) {
      return { 'aria-selected': true };
    }
    return isColumnSelectable ? { 'aria-selected': false } : undefined;
  };

  const getAriaSort = () => {
    if (sortDirection === 'ascending' || sortDirection === 'descending') {
      return { 'aria-sort': sortDirection };
    }
    return undefined;
  };

  return (
    <th
      aria-colindex={columnIndex + 1}
      {...getAriaSelected()}
      {...getAriaSort()}
      class={cellClasses}
      id={useId()}
      tabIndex={-1}
      role={'columnheader'}
      data-oj-cell-type={'header'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-header-cell={tableId}
      {...(typeof columnKey === 'number' && { 'data-oj-column-key-type': 'number' })}
      {...(isColumnSelectable && interactionProps)}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        {getHeaderCellRenderer()(headerCellRendererProps)}
      </TabbableModeContext.Provider>
    </th>
  );
}
